import { Capacitor } from "@capacitor/core"
import { FirebaseMessaging } from "@capacitor-firebase/messaging"
import { PluginListenerHandle } from "@capacitor/core"

import { unregisterMobileDeviceRequest } from "../../Module/Notifications/PushNotificationServiceRequests"
import { registerMobileDeviceRequest } from "../../Module/Notifications/PushNotificationServiceRequests"
import { ToastService } from "./ToastService"

const PUSH_TOKEN_STORAGE_KEY = "pushToken"
const FOREGROUND_DUPLICATE_WINDOW_MS = 12000
const FOREGROUND_DUPLICATE_CACHE_TTL_MS = 60000

export type PushNotificationActionData = {
    notification_id?: string | number
    redirect_url?: string
    notification_type?: string
    organization_id?: string | number
    room_id?: string | number
    post_id?: string | number
    invitation_id?: string | number
    event_creator_id?: string | number
    conversation_id?: string | number
    message_id?: string | number
    sender_id?: string | number
}

export class PushNotificationsManager {
    private initialized = false
    private initializePromise: Promise<void> | null = null
    private onPushRedirect?: (redirectUrl: string, data?: PushNotificationActionData) => void
    private onForegroundNotification?: (data: PushNotificationActionData) => void
    private shouldSuppressForegroundToast?: (data: PushNotificationActionData) => boolean
    private unregisterOnMessage: PluginListenerHandle | null = null
    private unregisterOnTokenReceived: PluginListenerHandle | null = null
    private unregisterOnNotificationAction: PluginListenerHandle | null = null
    private recentForegroundNotificationKeys = new Map<string, number>()

    async initialize(
        onPushRedirect?: (redirectUrl: string, data?: PushNotificationActionData) => void,
        onForegroundNotification?: (data: PushNotificationActionData) => void,
        shouldSuppressForegroundToast?: (data: PushNotificationActionData) => boolean,
    ): Promise<void> {
        this.onPushRedirect = onPushRedirect
        this.onForegroundNotification = onForegroundNotification
        this.shouldSuppressForegroundToast = shouldSuppressForegroundToast

        if (!Capacitor.isNativePlatform()) {
            return
        }

        if (this.initialized) {
            return
        }

        if (this.initializePromise) {
            await this.initializePromise
            return
        }

        this.initializePromise = this.initializeInternal()
        try {
            await this.initializePromise
        } finally {
            this.initializePromise = null
        }
    }

    private async initializeInternal(): Promise<void> {
        this.removeListeners()

        try {
            await FirebaseMessaging.requestPermissions()

            this.unregisterOnTokenReceived = await FirebaseMessaging.addListener("tokenReceived", async event => {
                const token = event.token
                if (!token) {
                    return
                }
                localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token)
                if (this.hasAccessToken()) {
                    await this.registerToken(token)
                }
            })

            this.unregisterOnMessage = await FirebaseMessaging.addListener("notificationReceived", event => {
                const title = event.notification?.title || "Neue Benachrichtigung"
                const body = event.notification?.body
                const notificationData = (event.notification?.data || {}) as PushNotificationActionData

                if (this.isDuplicateForegroundNotification(title, body, notificationData)) {
                    return
                }

                const shouldSuppressToast = this.shouldSuppressForegroundToast?.(notificationData) || false
                if (!shouldSuppressToast) {
                    const redirectUrl = notificationData?.redirect_url
                    const hasActionTarget = Boolean(
                        redirectUrl ||
                        notificationData?.notification_type ||
                        notificationData?.conversation_id ||
                        notificationData?.room_id ||
                        notificationData?.post_id
                    )
                    ToastService.info({
                        title,
                        description: body,
                        duration: 3500,
                        action: hasActionTarget && this.onPushRedirect
                            ? {
                                label: "Oeffnen",
                                onClick: () => {
                                    this.onPushRedirect?.(String(redirectUrl || ""), notificationData)
                                },
                            }
                            : undefined,
                    })
                }

                if (this.onForegroundNotification) {
                    this.onForegroundNotification(notificationData)
                }
            })

            this.unregisterOnNotificationAction = await FirebaseMessaging.addListener("notificationActionPerformed", event => {
                const notificationData = (event.notification?.data || {}) as PushNotificationActionData
                const redirectUrl = notificationData.redirect_url
                if (this.onPushRedirect) {
                    this.onPushRedirect(String(redirectUrl || ""), notificationData)
                }
            })

            const tokenResponse = await FirebaseMessaging.getToken()
            if (tokenResponse?.token) {
                localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, tokenResponse.token)
                if (this.hasAccessToken()) {
                    await this.registerToken(tokenResponse.token)
                }
            }

            this.initialized = true
        } catch (error) {
            console.warn("Failed to initialize push notifications", error)
        }
    }

    async syncRegistration(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return
        }

        if (!this.hasAccessToken()) {
            return
        }

        const storedToken = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY)
        if (storedToken) {
            await this.registerToken(storedToken)
            return
        }

        const tokenResponse = await FirebaseMessaging.getToken()
        if (tokenResponse?.token) {
            await this.registerToken(tokenResponse.token)
        }
    }

    async teardown(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return
        }

        try {
            const token = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY)
            if (token) {
                await unregisterMobileDeviceRequest(token)
                localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY)
            }
        } catch (error) {
            console.warn("Failed to unregister push token", error)
        }

        this.removeListeners()
        this.initialized = false
    }

    private async registerToken(token: string) {
        const platform = Capacitor.getPlatform()
        if (platform !== "ios" && platform !== "android") {
            return
        }

        await registerMobileDeviceRequest({
            fcm_token: token,
            platform,
            notifications_enabled: true,
            app_version: process.env.REACT_APP_VERSION || "unknown",
        })

        localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token)
    }

    private hasAccessToken() {
        return Boolean(localStorage.getItem("accessToken"))
    }

    private removeListeners() {
        this.unregisterOnMessage?.remove()
        this.unregisterOnTokenReceived?.remove()
        this.unregisterOnNotificationAction?.remove()
        this.unregisterOnMessage = null
        this.unregisterOnTokenReceived = null
        this.unregisterOnNotificationAction = null
    }

    private isDuplicateForegroundNotification(title: string, body: string | undefined, data: PushNotificationActionData): boolean {
        const now = Date.now()
        const key = this.buildForegroundNotificationKey(title, body, data)
        const previousSeenAt = this.recentForegroundNotificationKeys.get(key)

        this.pruneForegroundNotificationCache(now)

        if (typeof previousSeenAt === "number" && now - previousSeenAt < FOREGROUND_DUPLICATE_WINDOW_MS) {
            return true
        }

        this.recentForegroundNotificationKeys.set(key, now)
        return false
    }

    private buildForegroundNotificationKey(title: string, body: string | undefined, data: PushNotificationActionData): string {
        const keyData = {
            notification_id: data.notification_id || "",
            message_id: data.message_id || "",
            conversation_id: data.conversation_id || "",
            room_id: data.room_id || "",
            post_id: data.post_id || "",
            invitation_id: data.invitation_id || "",
            event_creator_id: data.event_creator_id || "",
            sender_id: data.sender_id || "",
            notification_type: data.notification_type || "",
            redirect_url: data.redirect_url || "",
            title,
            body: body || "",
        }

        return JSON.stringify(keyData)
    }

    private pruneForegroundNotificationCache(now: number) {
        this.recentForegroundNotificationKeys.forEach((seenAt, key) => {
            if (now - seenAt > FOREGROUND_DUPLICATE_CACHE_TTL_MS) {
                this.recentForegroundNotificationKeys.delete(key)
            }
        })
    }
}

export const pushNotificationsManager = new PushNotificationsManager()
